import React from 'react';
import { useLocation } from 'react-router-dom';

import axios from '../../utils/axiosConfig';
import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Table from '../../components/Table';
import SelectBox from '../../components/SelectBox';
import Pagination from '../../components/Pagination';
import usePagination from '../../components/Pagination/usePagination';

import styles from './FileTransfers.module.css';

// Files Suricata reassembled off cleartext protocols (HTTP/FTP/SMB/SMTP/NFS),
// forwarded by the appliance IDS monitor. A row whose hash matched a client IOC
// is flagged — those also raise an alert at ingest.

const humanBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = b / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(1)} ${units[i]}`;
};

const FileTransfersScreen = () => {
  const [selectedWatcher, setSelectedWatcher] = React.useState('');
  const [searchKey, setSearchKey] = React.useState('');
  // Deep-link from the dashboard: ?q=<ip|filename> pre-fills the search.
  const location = useLocation();
  React.useEffect(() => {
    const q = new URLSearchParams(location.search).get('q');
    if (q) setSearchKey(q);
  }, [location.search]);
  const [loading, setLoading] = React.useState(false);
  const [watchers, setWatchers] = React.useState([]);
  const [files, setFiles] = React.useState([]);
  const [filesSeen, setFilesSeen] = React.useState(0);
  const [iocMatches, setIocMatches] = React.useState(0);

  // Watcher dropdown options, loaded once on mount. An empty watcher makes the
  // backend return just the list (no file scan), so the selector populates while
  // the table stays empty until the user picks a watcher.
  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.post('/api/file-transfers',
          { watcherId: '' }, { headers: { 'Content-Type': 'application/json' } });
        if (active && data && Array.isArray(data.watchers)) setWatchers(data.watchers);
      } catch (e) { /* leave the dropdown empty on failure */ }
    })();
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    let active = true;
    (async () => {
      if (!selectedWatcher) return;   // nothing selected yet — render with no data
      setLoading(true);
      try {
        const { data } = await axios.post('/api/file-transfers',
          { watcherId: selectedWatcher }, { headers: { 'Content-Type': 'application/json' } });
        if (!active) return;
        if (data && data.status) {
          const rows = Array.isArray(data.files) ? data.files : [];
          setFiles(rows);
          setFilesSeen(typeof data.filesSeen === 'number' ? data.filesSeen
            : rows.reduce((sum, r) => sum + (r.count || 1), 0));
          setIocMatches(data.iocMatches || 0);
          if (Array.isArray(data.watchers)) setWatchers(data.watchers);
        }
      } catch (e) { /* leave the table empty on failure */ }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [selectedWatcher]);

  const filtered = React.useMemo(() => {
    const key = searchKey.trim().toLowerCase();
    if (!key) return files;
    return files.filter((f) => [f.filename, f.type, f.protocol, f.src, f.dst, f.sha256, f.watcher]
      .some((v) => String(v ?? '').toLowerCase().includes(key)));
  }, [files, searchKey]);

  const { pageRows, pageNumber, totalRows, pageLimit, handlePageChange } = usePagination(filtered);

  return (
    <Content
      currentMenu='file-transfers'
      headerTitle='File Transfers'
      searchBox={true}
      searchPlaceholder='Search filename, type, IP, hash'
      searchValue={searchKey}
      searchOnChange={(e) => setSearchKey(e.target.value)}
    >
      <div className={styles.intro}>
        Files the appliance's IDS engine reassembled off cleartext protocols
        (HTTP, FTP, SMB, SMTP, NFS). Repeated transfers of the same file between
        the same two hosts are grouped into one row — <strong>Count</strong> is
        how many times it was seen and <strong>Total Size</strong> the bytes
        across them. A row flagged <strong>IOC</strong> matched a known-bad hash
        on your IOC lists and also raised an alert. Encrypted transfers (HTTPS,
        SFTP) are not visible here — their volume shows on the SOC exfil/throughput
        detectors.
      </div>

      <div className={styles.summary_grid}>
        <div className={styles.summary_card}>
          <span className={styles.summary_value}>{filesSeen.toLocaleString()}</span>
          <span className={styles.summary_label}>Files Seen</span>
        </div>
        <div className={styles.summary_card}>
          <span className={styles.summary_value}>{files.length.toLocaleString()}</span>
          <span className={styles.summary_label}>Unique Transfers</span>
        </div>
        <div className={`${styles.summary_card} ${iocMatches ? styles.danger : ''}`}>
          <span className={styles.summary_value}>{iocMatches}</span>
          <span className={styles.summary_label}>IOC Matches</span>
        </div>
      </div>

      <div className={styles.control}>
        <SelectBox label='Watcher' value={selectedWatcher} onChange={(e) => setSelectedWatcher(e.target.value)}>
          <option value=''>Select a Watcher</option>
          <option value='All Watchers'>All Watchers</option>
          {watchers.map((w) => (w.length >= 2 ? (
            <option value={w[0]} key={w[0]}>{w[1]}</option>
          ) : null))}
        </SelectBox>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className={styles.table_container}>
          <Table
            containerStyle={{ height: '100%', flex: '1 1 auto', overflow: 'auto' }}
            tableHead={
              <tr>
                <th className={styles.nowrap}>Last Seen</th>
                <th className={styles.nowrap}>Watcher</th>
                <th className={styles.filename_col}>Filename</th>
                <th className={styles.nowrap}>Type</th>
                <th className={styles.num_col}>Count</th>
                <th className={styles.num_col}>Total Size</th>
                <th className={styles.nowrap}>Protocol</th>
                <th className={styles.nowrap}>Source</th>
                <th className={styles.nowrap}>Destination</th>
                <th className={styles.nowrap}>SHA-256</th>
                <th className={styles.nowrap}>IOC</th>
              </tr>
            }
            tableRow={
              pageRows.length > 0 ? (
                pageRows.map((f, i) => (
                  <tr key={i} className={f.iocMatch ? styles.ioc_row : undefined}>
                    <td className={styles.nowrap}>{f.time}</td>
                    <td className={styles.nowrap}>{f.watcher}</td>
                    <td className={styles.filename} title={f.filename}>{f.filename || '—'}</td>
                    <td className={styles.nowrap}>{f.type || '—'}</td>
                    <td className={styles.num_col}>{(f.count || 1) > 1 ? `×${(f.count).toLocaleString()}` : '1'}</td>
                    <td className={styles.num_col}>{humanBytes(f.size)}</td>
                    <td className={styles.nowrap}>{f.protocol || '—'}</td>
                    <td className={styles.mono}>{f.src || '—'}</td>
                    <td className={styles.mono}>{f.dst || '—'}</td>
                    <td className={styles.hash} title={f.sha256}>{f.sha256 ? `${f.sha256.slice(0, 16)}…` : '—'}</td>
                    <td className={styles.nowrap}>{f.iocMatch ? <span className={styles.ioc_badge}>⚠ IOC</span> : ''}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={11} style={{ textAlign: 'center' }}>
                  No file transfers seen yet. Enable Suricata fileinfo on the appliance to capture them.
                </td></tr>
              )
            }
          />
          <div className='paginationContainer'>
            <Pagination
              onPageChange={handlePageChange}
              rowsPerPage={pageLimit}
              totalPageSize={totalRows}
              pageNumber={pageNumber}
            />
          </div>
        </div>
      )}
    </Content>
  );
};

export default FileTransfersScreen;
